/*
 * Copyright Higharc Inc. 2019
 * Partially ported to JavaScript/GLSL from https://github.com/google/spherical-harmonics which was licensed under Apache License Version 2.0
 * We use the same SH constants as Google's SH library.
 */
import { Vector3 } from "three";
import { CubeCameraUtil } from "./CubeCameraUtil";

export const SH = {
  getCoefficientVectorCount: function (shOrder) {
    return (shOrder + 1) * (shOrder + 1);
  },

  getCoefficientVectorIndex: function (l, m) {
    return l * l + l + m;
  },

  initSHCoefficients: function (shOrder, populateWithTestData) {
    const shCoefficients: Vector3[] = [];

    const shCoeffCount = SH.getCoefficientVectorCount(shOrder);
    for (let i = 0; i < shCoeffCount; ++i) {
      shCoefficients[i] = new Vector3();
    }

    if (populateWithTestData) {
      const fixedTestCoefficients = [
        new Vector3(0, 0, 0),
        new Vector3(1, 0, 0),
        new Vector3(0, 1, 0),
        new Vector3(0, 0, -1),
        new Vector3(0.5, 0.1, 0.1),
        new Vector3(0.1, 0.5, 0.1),
        new Vector3(-0.5, -0.5, -0.5),
        new Vector3(0.1, 0.1, 0.5),
        new Vector3(0.4, 0.4, 0.4),
      ];
      for (let i = 1; i < Math.min(shCoeffCount, fixedTestCoefficients.length); ++i) {
        shCoefficients[i].copy(fixedTestCoefficients[i]);
      }
      for (let i = fixedTestCoefficients.length; i < shCoeffCount; ++i) {
        shCoefficients[i].x = (Math.random() * 3.0) / i;
        shCoefficients[i].y = (Math.random() * 3.0) / i;
        shCoefficients[i].z = (Math.random() * 3.0) / i;
      }

      shCoefficients[0].x = Math.random() * 3.0;
      shCoefficients[0].y = Math.random() * 3.0;
      shCoefficients[0].z = Math.random() * 3.0;
    }
    return shCoefficients;
  },

  // l and m identify the spherical harmonics basis function.
  // dir should be a normalized Vector3.
  evalSHBasisFunction: function (l, m, dir) {
    switch (l) {
      case 0:
        // 0.5 * sqrt(1/pi)
        return 0.282095;
      case 1:
        switch (m) {
          case -1:
            // -sqrt(3/(4pi)) * y
            return -0.488603 * dir.y;
          case 0:
            // sqrt(3/(4pi)) * z
            return 0.488603 * dir.z;
          case 1:
            // -sqrt(3/(4pi)) * x
            return -0.488603 * dir.x;
        }
        break;
      case 2:
        switch (m) {
          case -2:
            // 0.5 * sqrt(15/pi) * x * y
            return 1.092548 * dir.x * dir.y;
          case -1:
            // -0.5 * sqrt(15/pi) * y * z
            return -1.092548 * dir.y * dir.z;
          case 0:
            // 0.25 * sqrt(5/pi) * (-x^2-y^2+2z^2)
            return 0.315392 * (-dir.x * dir.x - dir.y * dir.y + 2.0 * dir.z * dir.z);
          case 1:
            // -0.5 * sqrt(15/pi) * x * z
            return -1.092548 * dir.x * dir.z;
          case 2:
            // 0.25 * sqrt(15/pi) * (x^2 - y^2)
            return 0.546274 * (dir.x * dir.x - dir.y * dir.y);
        }
        break;
      case 3:
        switch (m) {
          case -3:
            // -0.25 * sqrt(35/(2pi)) * y * (3x^2 - y^2)
            return -0.590044 * dir.y * (3.0 * dir.x * dir.x - dir.y * dir.y);
          case -2:
            // 0.5 * sqrt(105/pi) * x * y * z
            return 2.890611 * dir.x * dir.y * dir.z;
          case -1:
            // -0.25 * sqrt(21/(2pi)) * y * (4z^2-x^2-y^2)
            return -0.457046 * dir.y * (4.0 * dir.z * dir.z - dir.x * dir.x - dir.y * dir.y);
          case 0:
            // 0.25 * sqrt(7/pi) * z * (2z^2 - 3x^2 - 3y^2)
            return 0.373176 * dir.z * (2.0 * dir.z * dir.z - 3.0 * dir.x * dir.x - 3.0 * dir.y * dir.y);
          case 1:
            // -0.25 * sqrt(21/(2pi)) * x * (4z^2-x^2-y^2)
            return -0.457046 * dir.x * (4.0 * dir.z * dir.z - dir.x * dir.x - dir.y * dir.y);
          case 2:
            // 0.25 * sqrt(105/pi) * z * (x^2 - y^2)
            return 1.445306 * dir.z * (dir.x * dir.x - dir.y * dir.y);
          case 3:
            // -0.25 * sqrt(35/(2pi)) * x * (x^2-3y^2)
            return -0.590044 * dir.x * (dir.x * dir.x - 3.0 * dir.y * dir.y);
        }
        break;
      case 4:
        switch (m) {
          case -4:
            // 0.75 * sqrt(35/pi) * x * y * (x^2-y^2)
            return 2.503343 * dir.x * dir.y * (dir.x * dir.x - dir.y * dir.y);
          case -3:
            // -0.75 * sqrt(35/(2pi)) * y * z * (3x^2-y^2)
            return -1.770131 * dir.y * dir.z * (3.0 * dir.x * dir.x - dir.y * dir.y);
          case -2:
            // 0.75 * sqrt(5/pi) * x * y * (7z^2-1)
            return 0.946175 * dir.x * dir.y * (7.0 * dir.z * dir.z - 1.0);
          case -1:
            // -0.75 * sqrt(5/(2pi)) * y * z * (7z^2-3)
            return -0.669047 * dir.y * dir.z * (7.0 * dir.z * dir.z - 3.0);
          case 0:
            // 3/16 * sqrt(1/pi) * (35z^4-30z^2+3)
            const z2 = dir.z * dir.z;
            return 0.105786 * (35.0 * z2 * z2 - 30.0 * z2 + 3.0);
          case 1:
            // -0.75 * sqrt(5/(2pi)) * x * z * (7z^2-3)
            return -0.669047 * dir.x * dir.z * (7.0 * dir.z * dir.z - 3.0);
          case 2:
            // 3/8 * sqrt(5/pi) * (x^2 - y^2) * (7z^2 - 1)
            return 0.473087 * (dir.x * dir.x - dir.y * dir.y) * (7.0 * dir.z * dir.z - 1.0);
          case 3:
            // -0.75 * sqrt(35/(2pi)) * x * z * (x^2 - 3y^2)
            return -1.770131 * dir.x * dir.z * (dir.x * dir.x - 3.0 * dir.y * dir.y);
          case 4:
            // 3/16*sqrt(35/pi) * (x^2 * (x^2 - 3y^2) - y^2 * (3x^2 - y^2))
            const x2 = dir.x * dir.x;
            const y2 = dir.y * dir.y;
            return 0.625836 * (x2 * (x2 - 3.0 * y2) - y2 * (3.0 * x2 - y2));
        }
        break;
    }
    throw "Unsupported SH basis function l " + l + " m " + m;
  },

  addSampleToCoefficients: function (shOrder, coefficients, direction, texel) {
    for (let l = 0; l <= shOrder; ++l) {
      for (let m = -l; m <= l; ++m) {
        const basisFunctionSample = SH.evalSHBasisFunction(l, m, direction);
        const vectorIndex = SH.getCoefficientVectorIndex(l, m);
        for (let channel = 0; channel < texel.length; ++channel) {
          coefficients[vectorIndex][channel] += basisFunctionSample * texel[channel];
        }
      }
    }
  },

  // cubeFaces: list of cube map face data in the following order: +x, -x, +y, -y, +z, -z.
  // Each face should be stored as an array of pixels, with channels from each pixel packed next to each other.
  // Both the source and the destination can contain an arbitrary amount of channels.
  //
  // Basically we numerically integrate the function for determining each SH coefficient c:
  //   c = integrate f (theta, phi) * y_l_m(theta, phi) dOmega
  // where f is the environment map value in the ray direction determined by angles theta and phi, and
  // y_l_m is one of the lth order spherical harmonics functions.
  // This needs to be done over an equal distribution of angles of the unit sphere.
  // http://simonstechblog.blogspot.com/2011/12/spherical-harmonic-lighting.html
  getCoefficientsFromCubeMap: function (cubeFaces, cubeTextureWidth, shOrder, numChannelsSrc, numChannelsDst) {
    numChannelsDst = numChannelsDst !== undefined ? numChannelsDst : 3;

    const coefficients: Float32Array[] = [];
    const coefficientCount = SH.getCoefficientVectorCount(shOrder);
    for (let i = 0; i < coefficientCount; ++i) {
      coefficients.push(new Float32Array(numChannelsDst));
    }

    // Half of the width of a texel in coordinates ranging from -1 to 1.
    const texelHalfWidth = 1.0 / cubeTextureWidth;

    const direction = new Vector3(0, 0, 0);

    // Reference for how cube map face textures are oriented: https://scalibq.wordpress.com/2013/06/23/cubemaps/

    const texel = new Float32Array(numChannelsDst);

    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      const cubeFace = cubeFaces[faceIndex];
      let texelCenterX = -1.0 + texelHalfWidth;
      for (let intX = 0; intX < cubeTextureWidth; ++intX) {
        let texelCenterY = 1.0 - texelHalfWidth;
        for (let intY = 0; intY < cubeTextureWidth; ++intY) {
          CubeCameraUtil.getCubeMapTexelDirection(faceIndex, texelCenterX, texelCenterY, direction);
          // We weigh each cube map texel by the solid angle they cover.
          // The sum of all of our weights is the solid angle covering the whole unit sphere, which should be
          // correct, so additional weighting is not needed.
          const texelSolidAngle = CubeCameraUtil.cubeMapTexelSolidAngle(texelCenterX, texelCenterY, texelHalfWidth);
          const firstChannelSrcIndex = (intY * cubeTextureWidth + intX) * numChannelsSrc;
          for (let channel = 0; channel < numChannelsDst; ++channel) {
            texel[channel] = cubeFace[firstChannelSrcIndex + channel] * texelSolidAngle;
          }
          // We evaluate the SH basis function just at the texel center.
          // This can be interpreted as treating each cube map texel as a point sample.
          SH.addSampleToCoefficients(shOrder, coefficients, direction, texel);
          texelCenterY -= texelHalfWidth * 2.0;
        }
        texelCenterX += texelHalfWidth * 2.0;
      }
    }
    return coefficients;
  },
};
